import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface PaginationState {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export type PaginationItem =
  | {
      type: 'page';
      page: number;
      active: boolean;
    }
  | {
      type: 'ellipsis';
      key: string;
    };

@Component({
  selector: 'app-pagination',
  standalone: false,
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
})
export class PaginationComponent {
  @Input() pagination: PaginationState = {
    page: 1,
    perPage: 25,
    total: 0,
    totalPages: 0,
  };

  @Input() loading = false;

  @Input() perPageOptions: number[] = [10, 20, 25, 50, 100];

  /**
   * How many page numbers to show on each side of the current page.
   *
   * Example with sideCount = 3 and current page = 9:
   * 1, ..., 6, 7, 8, [9], 10, 11, 12, ..., 34
   */
  @Input() sideCount = 3;

  @Output() pageChange = new EventEmitter<number>();
  @Output() perPageChange = new EventEmitter<number>();

  get currentPage(): number {
    return Math.max(1, Number(this.pagination?.page || 1));
  }

  get perPage(): number {
    return Math.max(1, Number(this.pagination?.perPage || 25));
  }

  get total(): number {
    return Math.max(0, Number(this.pagination?.total || 0));
  }

  get totalPages(): number {
    return Math.max(0, Number(this.pagination?.totalPages || 0));
  }

  get hasRecords(): boolean {
    return this.total > 0;
  }

  get showingFrom(): number {
    if (!this.hasRecords) {
      return 0;
    }

    return (this.currentPage - 1) * this.perPage + 1;
  }

  get showingTo(): number {
    if (!this.hasRecords) {
      return 0;
    }

    return Math.min(this.currentPage * this.perPage, this.total);
  }

  get canGoPrevious(): boolean {
    return !this.loading && this.currentPage > 1;
  }

  get canGoNext(): boolean {
    return !this.loading && this.totalPages > 0 && this.currentPage < this.totalPages;
  }

  get pageItems(): PaginationItem[] {
    return this.buildPageItems();
  }

  previousPage(): void {
    if (!this.canGoPrevious) {
      return;
    }

    this.goToPage(this.currentPage - 1);
  }

  nextPage(): void {
    if (!this.canGoNext) {
      return;
    }

    this.goToPage(this.currentPage + 1);
  }

  goToPage(page: number): void {
    const normalizedPage = Math.max(1, Math.min(page, this.totalPages || 1));

    if (normalizedPage === this.currentPage || this.loading) {
      return;
    }

    this.pageChange.emit(normalizedPage);
  }

  onPerPageChange(value: number | string): void {
    const parsed = Number(value);

    if (!Number.isFinite(parsed) || parsed <= 0 || this.loading) {
      return;
    }

    this.perPageChange.emit(parsed);
  }

  trackByPaginationItem(index: number, item: PaginationItem): string {
    if (item.type === 'ellipsis') {
      return item.key;
    }

    return `page-${item.page}`;
  }

  private buildPageItems(): PaginationItem[] {
    const totalPages = this.totalPages;
    const currentPage = Math.min(this.currentPage, totalPages || 1);

    if (totalPages <= 0) {
      return [];
    }

    const visiblePages = new Set<number>();

    visiblePages.add(1);
    visiblePages.add(totalPages);

    const start = Math.max(1, currentPage - this.sideCount);
    const end = Math.min(totalPages, currentPage + this.sideCount);

    for (let page = start; page <= end; page++) {
      visiblePages.add(page);
    }

    const sortedPages = Array.from(visiblePages).sort((left, right) => left - right);

    const items: PaginationItem[] = [];

    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previousPage = sortedPages[index - 1];

      if (previousPage && page - previousPage > 1) {
        items.push({
          type: 'ellipsis',
          key: `ellipsis-${previousPage}-${page}`,
        });
      }

      items.push({
        type: 'page',
        page,
        active: page === currentPage,
      });
    }

    return items;
  }
}